/** Blue Max simulation (C). World coordinates and per-run state; no browser I/O. */
export const WIDTH = 256;
export const HEIGHT = 288;
export const COURSE_LENGTH = 3000;
export const CRUISE_SPEED = 48;
export const TAKEOFF_SPEED = 36;
export const MAX_ALTITUDE = 70;
export const MAX_BOMBS = 30;
export const MAX_CONDITION = 4;
export const READY_MS = 1000;
export const MISSION_CLEAR_MS = 1500;
export const RUNWAY_X = 155;
export const RUNWAY_HALF_WIDTH = 22;
export const SERVICE_START = 1900;
export const SERVICE_END = 2170;
export const MAX_MISSION_SCORE = 11000;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export function createCourse(mission, rng) {
    const targets = Array.from({ length: 18 }, (_, i) => {
        const type = ['building', 'gun', 'boat', 'bridge'][i % 4];
        const river = type === 'boat' || type === 'bridge';
        return {
            type,
            x: river ? 72 : (i % 2 ? 125 : 187) + (rng() - 0.5) * 12,
            y: i < 12 ? 420 + i * 100 : 2350 + (i - 12) * 90,
            z: 0,
            width: type === 'bridge' ? 30 : 10,
            depth: type === 'bridge' ? 9 : 12,
            height: type === 'building' ? 22 : 8,
            points: type === 'bridge' ? 300 : 200,
            marked: [3, 9, 16].includes(i),
            alive: true,
            cooldown: 1 + rng(),
        };
    });
    const fighters = Array.from({ length: Math.min(12, 4 + mission * 2) }, (_, i) => ({
        type: 'fighter',
        x: 30 + rng() * 170,
        y: i < 8 ? 480 + i * 115 : 2380 + (i - 8) * 85,
        z: 24 + rng() * 35,
        alive: true,
        active: false,
        cooldown: 1 + rng(),
    }));
    return { targets, fighters };
}

export function createGame({ rng = Math.random } = {}) {
    return {
        rng,
        ...createCourse(1, rng),
        plane: {
            x: RUNWAY_X, y: 0, z: 0, speed: 0,
            fuel: 100, bombs: MAX_BOMBS, condition: MAX_CONDITION,
            immunity: 0, gunCooldown: 0, bombCooldown: 0, dive: 0,
        },
        mission: 1,
        score: 0,
        status: 'ready',
        statusMs: READY_MS,
        elapsedMs: 0,
        bullets: [],
        bombs: [],
        explosions: [],
        events: [],
        runwayUsed: false,
        airfieldAnnounced: false,
        lowFuelAnnounced: false,
        takeoffEnd: 220,
        bombHeld: false,
        reason: null,
    };
}

function endGame(state, reason) {
    if (state.status === 'gameOver') return;
    state.status = 'gameOver';
    state.reason = reason;
    state.events.push({ type: 'gameOver' });
}

function damagePlane(state) {
    const p = state.plane;
    if (p.immunity > 0) return;
    p.condition -= 1;
    p.immunity = 1.5;
    state.events.push({ type: 'damage' });
    if (p.condition <= 0) endGame(state, 'damage');
}

function destroyTarget(state, target) {
    if (!target.alive) return;
    target.alive = false;
    state.score += target.type === 'fighter' ? 250 : target.points + (target.marked ? 200 : 0);
    state.explosions.push({ x: target.x, y: target.y, z: target.z, life: 0.5 });
    state.events.push({ type: 'impact' });
}

function onRunway(p, start, end, margin = 0) {
    return Math.abs(p.x - RUNWAY_X) <= RUNWAY_HALF_WIDTH - margin && p.y >= start && p.y <= end;
}

function enemyShot(state, enemy) {
    if (state.bullets.length >= 60) return;
    const p = state.plane;
    const dx = p.x - enemy.x;
    const dy = p.y - enemy.y;
    const z = enemy.type === 'fighter' ? enemy.z : enemy.height;
    const dz = p.z - z;
    const length = Math.hypot(dx, dy, dz) || 1;
    state.bullets.push({
        x: enemy.x, y: enemy.y, z, vx: dx / length * 95, vy: dy / length * 95,
        vz: dz / length * 95, enemy: true, life: 4,
    });
}

function updateEnemies(state, dt) {
    const p = state.plane;
    const difficulty = Math.min(6, state.mission);
    state.fighters.forEach((enemy) => {
        if (!enemy.alive) return;
        if (enemy.y - p.y < 250) enemy.active = true;
        if (!enemy.active) return;
        enemy.y -= (22 + difficulty * 2) * dt;
        enemy.x += clamp(p.x - enemy.x, -12, 12) * dt;
        enemy.cooldown -= dt;
        if (enemy.cooldown <= 0 && enemy.y > p.y && enemy.y < p.y + 180) {
            enemyShot(state, enemy);
            enemy.cooldown = 2.8 - difficulty * 0.2;
        }
        if (Math.abs(enemy.x - p.x) < 13 && Math.abs(enemy.y - p.y) < 13
            && Math.abs(enemy.z - p.z) < 9) damagePlane(state);
        if (enemy.y < p.y - 120) enemy.alive = false;
    });
    state.targets.forEach((target) => {
        if (!target.alive || target.type !== 'gun' || Math.abs(target.y - p.y) > 210) return;
        target.cooldown -= dt;
        if (target.cooldown <= 0) {
            enemyShot(state, target);
            target.cooldown = 3.4 - difficulty * 0.25;
        }
    });
}

function fireWeapons(state, input, bombPressed) {
    const p = state.plane;
    if (input.fire && p.gunCooldown <= 0 && state.bullets.length < 60) {
        state.bullets.push({ x: p.x, y: p.y + 12, z: p.z, vx: 0, vy: 240, vz: 0, life: 1.5 });
        p.gunCooldown = 0.16;
        state.events.push({ type: 'shoot' });
    }
    if (bombPressed && p.bombs > 0 && p.bombCooldown <= 0 && state.bombs.length < 8) {
        state.bombs.push({ x: p.x, y: p.y, z: p.z, speed: p.speed, fallSpeed: 8 });
        p.bombs -= 1;
        p.bombCooldown = 0.45;
        p.dive = 0.22;
        state.events.push({ type: 'bomb' });
    }
}

function updateProjectiles(state, dt) {
    const p = state.plane;
    state.bullets.forEach((bullet) => {
        bullet.x += bullet.vx * dt;
        bullet.y += bullet.vy * dt;
        bullet.z += bullet.vz * dt;
        bullet.life -= dt;
        if (bullet.life <= 0 || bullet.z < 0) return;
        if (bullet.enemy) {
            if (Math.abs(bullet.x - p.x) < 8 && Math.abs(bullet.y - p.y) < 9
                && Math.abs(bullet.z - p.z) < 7) {
                bullet.life = 0;
                damagePlane(state);
            }
            return;
        }
        const fighter = state.fighters.find(enemy => enemy.alive && enemy.active
            && Math.abs(enemy.x - bullet.x) < 12 && Math.abs(enemy.y - bullet.y) < 12
            && Math.abs(enemy.z - bullet.z) < 8);
        const ground = bullet.z <= 18 && state.targets.find(target => target.alive
            && Math.abs(target.x - bullet.x) < target.width && Math.abs(target.y - bullet.y) < target.depth);
        if (fighter || ground) {
            destroyTarget(state, fighter || ground);
            bullet.life = 0;
        }
    });
    state.bullets = state.bullets.filter(b => b.life > 0 && b.z >= 0 && Math.abs(b.y - p.y) < 400);
    state.bombs.forEach((bomb) => {
        bomb.y += bomb.speed * dt;
        bomb.fallSpeed += 34 * dt;
        bomb.z -= bomb.fallSpeed * dt;
        if (bomb.z > 0) return;
        state.targets.forEach((target) => {
            if (Math.abs(target.x - bomb.x) < target.width + 13
                && Math.abs(target.y - bomb.y) < target.depth + 13) destroyTarget(state, target);
        });
        state.explosions.push({ x: bomb.x, y: bomb.y, z: 0, life: 0.5 });
        state.events.push({ type: 'impact' });
    });
    state.bombs = state.bombs.filter(bomb => bomb.z > 0);
}

function startService(state) {
    state.status = 'servicing';
    state.plane.z = 0;
    state.plane.speed = 0;
    state.runwayUsed = true;
    state.bullets = [];
    state.bombs = [];
    state.events.push({ type: 'landed' });
}

function updateFlight(state, input, dt, bombPressed) {
    const p = state.plane;
    p.speed = Math.min(CRUISE_SPEED, p.speed + 24 * dt);
    p.x = clamp(p.x + ((input.right ? 1 : 0) - (input.left ? 1 : 0)) * 65 * dt, 15, 205);
    p.z = clamp(p.z + ((input.up ? 1 : 0) - (input.down ? 1 : 0)) * 28 * dt
        - (p.dive > 0 ? 23 * dt : 0), 0, MAX_ALTITUDE);
    p.y += p.speed * dt;
    if (!state.airfieldAnnounced && p.y >= SERVICE_START - 350) {
        state.airfieldAnnounced = true;
        state.events.push({ type: 'airfield' });
    }
    if (!state.runwayUsed && p.z <= 10 && onRunway(p, SERVICE_START, SERVICE_END - 100, 8)) {
        state.status = 'landing';
        state.bullets = [];
        state.bombs = [];
        return;
    }
    if (p.z <= 0) {
        endGame(state, 'crash');
        return;
    }
    const obstacle = state.targets.some(target => target.alive && p.z <= target.height
        && Math.abs(p.x - target.x) < target.width + 4 && Math.abs(p.y - target.y) < target.depth + 4);
    if (obstacle) {
        endGame(state, 'crash');
        return;
    }
    fireWeapons(state, input, bombPressed);
    updateEnemies(state, dt);
    updateProjectiles(state, dt);
    if (state.status === 'gameOver') return;
    if (p.y >= COURSE_LENGTH) {
        state.score += 1000;
        if (state.targets.filter(t => t.marked).every(t => !t.alive)) state.score += 1000;
        state.status = 'missionComplete';
        state.statusMs = MISSION_CLEAR_MS;
        state.events.push({ type: 'missionComplete' });
    }
}

function nextMission(state) {
    state.mission += 1;
    Object.assign(state, createCourse(state.mission, state.rng));
    state.plane.y = 0;
    state.status = 'flying';
    state.runwayUsed = false;
    state.airfieldAnnounced = false;
    state.bullets = [];
    state.bombs = [];
    state.explosions = [];
}

export function step(state, input = {}, dtMs = 1000 / 60) {
    if (state.status === 'gameOver' || !Number.isFinite(dtMs) || dtMs <= 0) return state;
    const ms = Math.min(dtMs, 100);
    const dt = ms / 1000;
    const p = state.plane;
    const bombPressed = !!input.bomb && !state.bombHeld;
    state.bombHeld = !!input.bomb;
    state.elapsedMs += ms;
    p.immunity = Math.max(0, p.immunity - dt);
    p.gunCooldown = Math.max(0, p.gunCooldown - dt);
    p.bombCooldown = Math.max(0, p.bombCooldown - dt);
    p.dive = Math.max(0, p.dive - dt);
    state.explosions.forEach(explosion => { explosion.life -= dt; });
    state.explosions = state.explosions.filter(explosion => explosion.life > 0).slice(-30);

    if (state.status === 'ready' || state.status === 'missionComplete') {
        state.statusMs -= ms;
        if (state.statusMs <= 0) {
            if (state.status === 'missionComplete') nextMission(state);
            else state.status = 'takeoff';
        }
        return state;
    }
    if (state.status === 'servicing') {
        p.fuel = Math.min(100, p.fuel + 20 * dt);
        p.bombs = Math.min(MAX_BOMBS, p.bombs + 6 * dt);
        p.condition = Math.min(MAX_CONDITION, p.condition + 0.8 * dt);
        if (p.fuel > 20) state.lowFuelAnnounced = false;
        if (input.depart || (p.fuel === 100 && p.bombs === MAX_BOMBS && p.condition === MAX_CONDITION)) {
            p.bombs = Math.floor(p.bombs);
            p.condition = Math.max(1, Math.floor(p.condition));
            state.status = 'takeoff';
            state.takeoffEnd = SERVICE_END;
        }
        return state;
    }
    p.fuel = Math.max(0, p.fuel - dt);
    if (p.fuel <= 0) {
        endGame(state, 'fuel');
        return state;
    }
    if (p.fuel <= 20 && !state.lowFuelAnnounced) {
        state.lowFuelAnnounced = true;
        state.events.push({ type: 'lowFuel' });
    }
    if (state.status === 'takeoff') {
        p.speed = Math.min(CRUISE_SPEED, p.speed + 18 * dt);
        p.y += p.speed * dt;
        if (input.up && p.speed >= TAKEOFF_SPEED) {
            p.z = 12;
            state.status = 'flying';
            state.events.push({ type: 'takeoff' });
        } else if (p.y > state.takeoffEnd) endGame(state, 'crash');
    } else if (state.status === 'landing') {
        p.speed = Math.max(12, p.speed - 35 * dt);
        p.y += p.speed * dt;
        p.z = Math.max(0, p.z - 12 * dt);
        p.x += (RUNWAY_X - p.x) * Math.min(1, dt * 4);
        if (!onRunway(p, SERVICE_START, SERVICE_END)) endGame(state, 'crash');
        else if (p.z === 0) startService(state);
    } else if (state.status === 'flying') {
        updateFlight(state, input, dt, bombPressed);
    }
    return state;
}
